/**
 * Bootstrap hook for prefetching Home data after login/home mount
 * 
 * Goal: Prefetch Home catalog and preferences in the background so that
 * when the user navigates to the Home page, the data is already in cache.
 * 
 * This provides a "big CRM" experience where the Home page appears almost instantly.
 * 
 * Usage:
 * ```tsx
 * const { company } = useParams<{ company: string }>();
 * const { client } = useClient();
 * 
 * useBootstrapHomeData({
 *   companyId: company,
 *   enabled: !!client && !!company,
 * });
 * ```
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { homeKeys } from "../lib/queryKeys";
import { getHomeCatalog, getHomePrefs } from "../api/homeApi";
import { queryOptions } from "../lib/queryClient";

type UseBootstrapHomeDataProps = {
  companyId: string | undefined;
  enabled?: boolean; // default true
};

export function useBootstrapHomeData({ 
  companyId, 
  enabled = true 
}: UseBootstrapHomeDataProps) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    if (!companyId) return;

    const catalogQueryKey = homeKeys.catalog(companyId);
    const prefsQueryKey = homeKeys.prefs(companyId);

    // Check if data already exists in cache (before prefetching)
    const existingCatalogData = queryClient.getQueryData(catalogQueryKey);
    const existingPrefsData = queryClient.getQueryData(prefsQueryKey);
    
    if (existingCatalogData && existingPrefsData) {
      return;
    }

    // Prefetch catalog in background - don't await, let it run silently
    if (!existingCatalogData) {
      queryClient.prefetchQuery({
        queryKey: catalogQueryKey,
        queryFn: async () => {
          const data = await getHomeCatalog(companyId);
          return data || [];
        },
        // Use same caching strategy as the actual query (must match useHomeCatalog)
        staleTime: 30 * 60 * 1000, // 30 minutes - matches queryOptions.config
        gcTime: 4 * 60 * 60 * 1000, // 4 hours - matches queryOptions.config
      }).catch((error) => {
        // Silently handle errors - prefetch failures shouldn't break the app
      });
    }

    // Prefetch prefs in background - don't await, let it run silently
    if (!existingPrefsData) {
      queryClient.prefetchQuery({
        queryKey: prefsQueryKey,
        queryFn: async () => {
          const data = await getHomePrefs(companyId);
          return data || { pinned: [], hidden: [], order: [], version: 1 };
        },
        // Use same caching strategy as the actual query (must match useHomePrefs)
        staleTime: 30 * 60 * 1000, // 30 minutes - matches queryOptions.config
        gcTime: 4 * 60 * 60 * 1000, // 4 hours - matches queryOptions.config
      }).catch((error) => {
        // Silently handle errors - prefetch failures shouldn't break the app
      });
    }
  }, [companyId, enabled, queryClient]);
}

